// Dropbox Browser Component for importing files from Dropbox
import React, { useState, useEffect, useCallback } from 'react';
import { Folder, FileAudio, Loader, Check } from 'lucide-react';
import {
  dropboxService,
  DropboxFile,
  DropboxFolder,
  DROPBOX_SESSION_EXPIRED_USER_MESSAGE,
} from '@services/dropbox.service';
import { cn } from '@utils/classNames';
import { formatBytes } from '@utils/format';
import { useToast } from '@components/ui';

interface DropboxBrowserProps {
  accessToken: string;
  onFileSelect: (files: File[]) => void;
  onCancel: () => void;
}

export const DropboxBrowser: React.FC<DropboxBrowserProps> = ({
  accessToken,
  onFileSelect,
  onCancel,
}) => {
  const [currentPath, setCurrentPath] = useState<string>('');
  const [files, setFiles] = useState<DropboxFile[]>([]);
  const [folders, setFolders] = useState<DropboxFolder[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<Map<string, number>>(new Map());

  const pathStack = currentPath.split('/').filter(Boolean);
  const { error: showError } = useToast();

  const handleExpiredDropboxSession = useCallback(() => {
    sessionStorage.removeItem('dropbox_access_token');
    dropboxService.clear();
    showError('Dropbox', DROPBOX_SESSION_EXPIRED_USER_MESSAGE);
    onCancel();
  }, [onCancel, showError]);

  const loadFolder = useCallback(
    async (path: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const metadata = await dropboxService.listFolder(path);
        setFiles(metadata.files);
        setFolders(metadata.folders);
        setCurrentPath(path);
      } catch (err: any) {
        console.error('❌ Failed to load Dropbox folder:', err);
        const msg = err?.message || 'Failed to load folder';
        if (msg === DROPBOX_SESSION_EXPIRED_USER_MESSAGE) {
          handleExpiredDropboxSession();
          return;
        }
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    },
    [handleExpiredDropboxSession]
  );

  // Initialize Dropbox service
  useEffect(() => {
    if (accessToken) {
      dropboxService.initialize(accessToken);
      loadFolder('');
    }
  }, [accessToken, loadFolder]);

  const handleFolderClick = (folder: DropboxFolder) => {
    loadFolder(folder.path_lower);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      loadFolder('');
    } else {
      const path = '/' + pathStack.slice(0, index + 1).join('/');
      loadFolder(path);
    }
  };

  const toggleFileSelection = (file: DropboxFile) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(file.path_lower)) {
        newSet.delete(file.path_lower);
      } else {
        newSet.add(file.path_lower);
      }
      return newSet;
    });
  };

  const handleImport = async () => {
    if (selectedFiles.size === 0) {
      setError('Please select at least one file.');
      return;
    }

    setIsDownloading(true);
    setError(null);
    const progressMap = new Map<string, number>();

    try {
      const filePromises = Array.from(selectedFiles).map(async (path) => {
        const file = files.find((f) => f.path_lower === path);
        if (!file) return null;

        progressMap.set(path, 0);
        setDownloadProgress(new Map(progressMap));

        try {
          const downloadedFile = await dropboxService.downloadFileAsFile(
            path,
            file.name
          );
          
          progressMap.set(path, 100);
          setDownloadProgress(new Map(progressMap));
          
          return downloadedFile;
        } catch (err: any) {
          console.error(`❌ Failed to download ${file.name}:`, err);
          progressMap.set(path, -1); // Error state
          setDownloadProgress(new Map(progressMap));
          throw err;
        }
      });

      const downloadedFiles = (await Promise.all(filePromises)).filter(
        (file): file is File => file !== null
      );

      if (downloadedFiles.length > 0) {
        onFileSelect(downloadedFiles);
      } else {
        setError('No files could be downloaded.');
      }
    } catch (err: any) {
      console.error('❌ Import error:', err);
      const msg = err?.message || 'Error importing files';
      if (msg === DROPBOX_SESSION_EXPIRED_USER_MESSAGE) {
        handleExpiredDropboxSession();
        return;
      }
      setError(msg);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(new Map());
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-dela text-xl text-white">Dropbox Import</h3>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all text-white font-inter"
        >
          Cancel
        </button>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <button
          onClick={() => handleBreadcrumbClick(-1)}
          className={cn(
            'flex items-center gap-1 px-3 py-1 rounded-lg transition-all',
            currentPath === ''
              ? 'bg-primary/20 text-primary font-bold'
              : 'bg-white/10 hover:bg-white/20 text-white'
          )}
        >
          <Folder className="w-4 h-4" />
          Root
        </button>
        {pathStack.map((segment, index) => (
          <React.Fragment key={index}>
            <span className="text-white/40">/</span>
            <button
              onClick={() => handleBreadcrumbClick(index)}
              className={cn(
                'px-3 py-1 rounded-lg transition-all',
                index === pathStack.length - 1
                  ? 'bg-primary/20 text-primary font-bold'
                  : 'bg-white/10 hover:bg-white/20 text-white'
              )}
            >
              {segment}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
          {error}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Folders */}
          {folders.length > 0 && (
            <div>
              <h4 className="font-codec font-bold text-white mb-2">Folders</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {folders.map((folder) => (
                  <button
                    key={folder.path_lower}
                    onClick={() => handleFolderClick(folder)}
                    className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-all text-left"
                  >
                    <Folder className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="font-inter text-white truncate">
                      {folder.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Files */}
          {files.length > 0 && (
            <div>
              <h4 className="font-codec font-bold text-white mb-2">
                Audio files ({files.length})
              </h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {files.map((file) => {
                  const isSelected = selectedFiles.has(file.path_lower);
                  const progress = downloadProgress.get(file.path_lower);
                  const isDownloadingFile = progress !== undefined && progress >= 0 && progress < 100;

                  return (
                    <div
                      key={file.path_lower}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer',
                        isSelected
                          ? 'bg-primary/20 border-primary'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      )}
                      onClick={() => toggleFileSelection(file)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileAudio className="w-5 h-5 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-inter font-bold text-white truncate">
                            {file.name}
                          </p>
                          <p className="text-sm text-white/60">
                            {formatBytes(file.size)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isDownloadingFile && (
                          <Loader className="w-4 h-4 text-primary animate-spin" />
                        )}
                        {progress === 100 && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                        {isSelected && !isDownloadingFile && progress !== 100 && (
                          <div className="w-4 h-4 rounded-full bg-primary border-2 border-primary" />
                        )}
                        {!isSelected && !isDownloadingFile && progress !== 100 && (
                          <div className="w-4 h-4 rounded-full border-2 border-white/40" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {folders.length === 0 && files.length === 0 && (
            <div className="text-center py-12 text-white/60">
              <Folder className="w-12 h-12 mx-auto mb-3 text-white/40" />
              <p>This folder is empty</p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {selectedFiles.size > 0 && !isDownloading && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
          <p className="font-inter text-white min-w-0">
            {selectedFiles.size} {selectedFiles.size === 1 ? 'file' : 'files'} selected
          </p>
          <button
            onClick={handleImport}
            className="w-full sm:w-auto px-6 py-2 min-h-[44px] bg-primary hover:bg-primary/90 rounded-lg transition-all text-black font-codec font-bold touch-manipulation"
          >
            Import
          </button>
        </div>
      )}

      {isDownloading && (
        <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-center">
          <Loader className="w-6 h-6 text-primary animate-spin mx-auto mb-2" />
          <p className="text-white/80">Downloading files...</p>
        </div>
      )}
    </div>
  );
};

